import React, { useState, useEffect, useRef } from 'react';
import { getDrivers, updateDriver } from '../traccar';
import { getSafetyDoc, saveSafetyDoc, fileToDataUrl } from '../photoStore';
import { DQ_ITEMS, itemStatus, readinessScore, STATUS_STYLE } from '../safetyModel';
import { playOpen, playClose } from '../clickSound';

const inp = { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', width: '100%', boxSizing: 'border-box' };

const ScoreRing = ({ score, size = 54 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const col = score >= 90 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="6" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="700" fill="#0f172a">{score}</text>
    </svg>
  );
};

export default function SafetyPage() {
  const [drivers, setDrivers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [data, setData] = useState({}); // working copy of the open driver's safety data
  const [docs, setDocs] = useState({}); // itemKey -> dataUrl
  const [error, setError] = useState(null);
  const docInput = useRef(null);
  const uploadItem = useRef(null);

  const load = async () => {
    try { setDrivers(await getDrivers()); setError(null); } catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const driver = drivers.find((d) => d.id === openId);
  const A = (driver && driver.attributes) || {};

  const open = async (d) => {
    if (openId === d.id) { playClose(); setOpenId(null); return; }
    playOpen();
    setOpenId(d.id);
    setData({ ...((d.attributes || {}).safety || {}) });
    // load any stored docs
    const found = {};
    await Promise.all(DQ_ITEMS.map(async (it) => { const doc = await getSafetyDoc(d.id, it.key); if (doc) found[it.key] = doc; }));
    setDocs(found);
  };

  const setItem = (key, patch) => setData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const save = async () => {
    const updated = { ...driver, attributes: { ...A, safety: data } };
    await updateDriver(updated);
    setDrivers((ds) => ds.map((d) => (d.id === driver.id ? updated : d)));
    playClose();
    setOpenId(null);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>⚠️ Safety & DOT Compliance</h1>
        <p>{error ? `🔴 ${error}` : 'Driver Qualification (DQ) files per 49 CFR Part 391. Click a driver to open their compliance file.'}</p>
      </div>

      <div className="card" style={{ background: '#eff6ff', borderColor: '#bfdbfe', marginBottom: '18px', padding: '12px 16px', fontSize: '13px', color: '#1e40af' }}>
        🛰️ <b>HOS / ELD logs</b> will appear here once a certified ELD partner is connected at deployment. This page manages the DQ file and compliance documents — it is a management tool, not a certified ELD.
      </div>

      {/* driver readiness grid */}
      <div className="grid grid-3">
        {drivers.map((d) => {
          const sc = readinessScore((d.attributes || {}).safety || {}, (d.attributes || {}).medicalExpiry);
          const flags = DQ_ITEMS.filter((i) => i.required && itemStatus(i, (d.attributes || {}).safety || {}, (d.attributes || {}).medicalExpiry) !== 'good').length;
          return (
            <div key={d.id} className="card" onClick={() => open(d)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <ScoreRing score={sc} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#0f172a' }}>{d.name}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>ID {d.uniqueId}</div>
                <div style={{ fontSize: '12px', marginTop: '4px', fontWeight: 600, color: flags === 0 ? '#15803d' : '#b45309' }}>
                  {flags === 0 ? '✓ DOT ready' : `${flags} item(s) need attention`}
                </div>
              </div>
            </div>
          );
        })}
        {drivers.length === 0 && !error && <p className="text-muted">No drivers yet — add drivers on the Drivers tab.</p>}
      </div>

      {/* DQ file editor */}
      {driver && (
        <div onClick={() => { playClose(); setOpenId(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: 'min(820px, 96vw)', boxSizing: 'border-box', padding: '22px', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <ScoreRing score={readinessScore(data, A.medicalExpiry)} size={64} />
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '20px' }}>{driver.name}</h2>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Driver Qualification File · ID {driver.uniqueId}</div>
              </div>
              <button onClick={() => { playClose(); setOpenId(null); }} style={{ background: '#f1f5f9', borderRadius: '50%', width: '30px', height: '30px', padding: 0, fontSize: '16px', color: '#475569' }}>×</button>
            </div>

            {DQ_ITEMS.map((item) => {
              const d = data[item.key] || {};
              const st = itemStatus(item, data, A.medicalExpiry);
              const s = STATUS_STYLE[st];
              return (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '10px', marginBottom: '6px', background: s.bg }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.dot, flexShrink: 0 }}></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{item.label}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{item.reg} · <span style={{ color: s.color, fontWeight: 700 }}>{s.text}</span>{docs[item.key] && ' · 📎 document on file'}</div>
                  </div>

                  {item.kind === 'check' && (
                    <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <input type="checkbox" checked={!!d.done} onChange={(e) => setItem(item.key, { done: e.target.checked })} /> Done
                    </label>
                  )}
                  {(item.kind === 'date' || item.kind === 'dateResult') && (
                    <input type="date" style={{ ...inp, width: '140px' }} value={d.date || ''} onChange={(e) => setItem(item.key, { date: e.target.value })} />
                  )}
                  {item.key === 'medical' && (
                    <input type="date" title="Expiration" style={{ ...inp, width: '140px' }} value={d.expiry || A.medicalExpiry || ''} onChange={(e) => setItem(item.key, { expiry: e.target.value })} />
                  )}
                  {item.kind === 'dateResult' && (
                    <select style={{ ...inp, width: '110px' }} value={d.result || ''} onChange={(e) => setItem(item.key, { result: e.target.value })}>
                      <option value="">Result…</option>
                      <option>Negative</option>
                      <option>Positive</option>
                      <option>Refused</option>
                    </select>
                  )}

                  <button
                    title="Attach document"
                    onClick={() => { uploadItem.current = item.key; docInput.current.click(); }}
                    style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: '6px', padding: '5px 9px', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}
                  >📎</button>
                </div>
              );
            })}

            <input ref={docInput} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async (e) => {
              const f = e.target.files[0]; e.target.value = '';
              if (!f || !uploadItem.current) return;
              try { const url = await fileToDataUrl(f); await saveSafetyDoc(driver.id, uploadItem.current, url); setDocs((p) => ({ ...p, [uploadItem.current]: url })); }
              catch (err) { alert(`Couldn't read the file: ${err.message}`); }
            }} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '9px 18px', borderRadius: '8px', fontWeight: 700 }} onClick={() => { playClose(); setOpenId(null); }}>Cancel</button>
              <button style={{ padding: '9px 18px', borderRadius: '8px', fontWeight: 700, background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white' }} onClick={save}>Save DQ file</button>
            </div>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>Dates and statuses save to the driver's server record; attached documents are stored in this browser for now and move to the server at deployment.</p>
          </div>
        </div>
      )}
    </div>
  );
}
